import { useState, useEffect, useCallback } from "react";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";

/**
 * Hook for managing MCP guidelines acceptance state.
 *
 * Fetches user preferences on mount and provides a function to accept the guidelines.
 * On error, defaults to accepted (don't block the UI).
 */
export function useMcpGuidelines(api: AssistantApi) {
  // null = loading, true/false = resolved
  const [hasAccepted, setHasAccepted] = useState<boolean | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    api
      .getUserPreferences()
      .then((prefs) => {
        if (!cancelled) {
          setHasAccepted(prefs.mcp_guidelines_accepted_at != null);
        }
      })
      .catch((error) => {
        console.error("Failed to load user preferences:", error);
        if (!cancelled) {
          // Default to accepted on error so UI isn't blocked
          setHasAccepted(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  const acceptGuidelines = useCallback(async () => {
    setIsAccepting(true);
    try {
      await api.updateUserPreferences({ accept_mcp_guidelines: true });
      setHasAccepted(true);
    } catch (error) {
      console.error("Failed to accept MCP guidelines:", error);
      // Still set accepted locally so user isn't stuck
      setHasAccepted(true);
    } finally {
      setIsAccepting(false);
    }
  }, [api]);

  return { hasAccepted, isAccepting, acceptGuidelines };
}
